import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  encryptPassword,
  loginWithCredentials,
  verify2FA as apiVerify2FA,
  fetchAdminMe,
  exchangeKeycloakCode,
  parseJWTClaims,
  type AdminUser
} from '../api/auth'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('admin_token'))
  const user = ref<AdminUser | null>(
    localStorage.getItem('admin_user') ? JSON.parse(localStorage.getItem('admin_user')!) : null
  )

  const isAuthenticated = computed(() => {
    return !!token.value
  })

  let lastVerifiedAt = 0

  function setSession(newToken: string, newUser: AdminUser, refreshToken?: string) {
    lastVerifiedAt = 0
    token.value = newToken
    user.value = newUser
    localStorage.setItem('admin_token', newToken)
    localStorage.setItem('admin_user', JSON.stringify(newUser))
    if (refreshToken) {
      localStorage.setItem('admin_refresh_token', refreshToken)
    }
  }

  function clearAuth() {
    lastVerifiedAt = 0
    token.value = null
    user.value = null
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_refresh_token')
    localStorage.removeItem('admin_user')
  }

  async function login(email: string, pass: string, secretCode?: string) {
    try {
      const encrypted = await encryptPassword(pass)
      const res = await loginWithCredentials(email, encrypted, secretCode)

      if (res.requires_2fa) {
        return {
          requires2FA: true,
          tempToken: res.temp_token,
          message: res.message
        }
      }

      if (res.success && res.access_token && res.admin) {
        const role = (res.admin.role || '').toLowerCase()
        if (role.includes('merchant')) {
          clearAuth()
          return { success: false, message: 'Access Denied: Merchant accounts cannot access the Admin Portal.' }
        }
        setSession(res.access_token, res.admin)
        return { success: true }
      }

      return { success: false, message: res.message || 'Login failed' }
    } catch (err: any) {
      return { success: false, message: err.response?.data?.message || err.message || 'Login failed' }
    }
  }

  async function verify2FACode(tempToken: string, code: string) {
    try {
      const res = await apiVerify2FA(tempToken, code)
      if (res.success && res.access_token && res.admin) {
        const role = (res.admin.role || '').toLowerCase()
        if (role.includes('merchant')) {
          clearAuth()
          return { success: false, message: 'Access Denied: Merchant accounts cannot access the Admin Portal.' }
        }
        setSession(res.access_token, res.admin)
        return { success: true }
      }
      return { success: false, message: res.message || 'Invalid 2FA code' }
    } catch (err: any) {
      return { success: false, message: err.response?.data?.message || err.message || '2FA verification failed' }
    }
  }

  function isTokenExpired(jwtToken: string): boolean {
    const claims = parseJWTClaims(jwtToken)
    if (!claims || typeof claims.exp !== 'number') return true
    return Date.now() >= claims.exp * 1000 - 30000
  }

  async function checkValidity(forceCheck = false): Promise<boolean> {
    if (!token.value) return false
    if (isTokenExpired(token.value)) {
      clearAuth()
      return false
    }

    const now = Date.now()
    if (!forceCheck && now - lastVerifiedAt < 60000 && user.value) {
      return true
    }

    try {
      const res = await fetchAdminMe()
      user.value = res.data
      localStorage.setItem('admin_user', JSON.stringify(res.data))
      lastVerifiedAt = now
      return true
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearAuth()
        return false
      }
      throw err
    }
  }

  async function refreshUserProfile() {
    if (!token.value) return
    await checkValidity(true)
  }

  function logout() {
    clearAuth()
    window.location.href = '/login'
  }

  async function handleSSOCallback(code: string) {
    try {
      const res = await exchangeKeycloakCode(code)
      if (res.success && res.access_token && res.admin) {
        setSession(res.access_token, res.admin, res.refresh_token)
        lastVerifiedAt = Date.now()
        return { success: true }
      }
      return { success: false, message: 'SSO code exchange failed' }
    } catch (err: any) {
      clearAuth()
      const status = err.response?.status
      const message = status === 403
        ? 'Access Denied: Your account does not have access to the Admin Portal.'
        : status === 401
          ? 'Your Keycloak session was rejected by the API. Please sign in again.'
          : err.message || 'SSO authentication failed'
      return { success: false, message }
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    setSession,
    clearAuth,
    login,
    verify2FACode,
    handleSSOCallback,
    checkValidity,
    refreshUserProfile,
    logout
  }
})
